/**
 * EditarAltaDialog — la fecha de incorporación de un alta abierta. SÓLO la fecha.
 *
 * Aquí decía «la fecha y el responsable», y el botón de la pantalla del alta se llamaba «Editar
 * fecha y responsable»: el diálogo nunca tuvo campo de responsable (14/10). Decisión de Iban: se
 * queda en la fecha, y el responsable, si hace falta cambiarlo, irá en la ficha de la persona.
 * El servidor (`editarAltaService`) sigue aceptando `responsable_id`; ninguna pantalla lo manda.
 *
 * ── POR QUÉ ES UN COMPONENTE Y NO UN MODAL SUELTO ─────────────────────────────────────────────
 *
 * Lo abren DOS pantallas: el banner del alta en la ficha de la persona y el panel del proceso.
 * Escribirlo dos veces es cómo se acaba con dos diálogos que avisan de cosas distintas del mismo
 * acto — que es exactamente lo que acababa de pasar con el aviso de la baja: uno decía la verdad
 * y el otro, la creencia de hace dos fases.
 *
 * ── CAMBIAR UNA FECHA NO PARECE UNA ACCIÓN QUE DÉ O QUITE ACCESO, Y LO ES ─────────────────────
 *
 * Desde Fase C el estado de la persona lo decide la fecha de incorporación. Cruzarla de futura a
 * pasada ACTIVA a esa persona en el acto; al revés, la deja sin poder entrar. Es el mismo caso que
 * la fecha de la baja en Fase 14, y se resuelve igual: **el aviso va ANTES, y nombra a la
 * persona**. Un aviso después es una notificación; antes es una decisión.
 *
 * El texto no se inventa aquí: sale de `efecto.accion`, que calcula el servidor. Adivinarlo en el
 * cliente comparando fechas daría un aviso que el servidor puede no cumplir —por el guardarraíl de
 * abajo— y diría que alguien recupera un acceso que no va a recuperar.
 *
 * ── EL GUARDARRAÍL, DICHO EN VOZ ALTA ─────────────────────────────────────────────────────────
 *
 * Si la persona está de baja o suspendida, editar la fecha NO la devuelve a la vida: la baja gana,
 * igual que en el banner. El servidor lo aplica y aquí se AVISA antes, porque si no el usuario
 * cambiaría la fecha esperando que entrara y no pasaría nada visible.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CalendarClock, UserCheck, UserX, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
// `Button` vive en ui-v2 y `Modal` en ui-v2.5: son dos paquetes distintos que conviven. Importar
// los dos del mismo sitio compila y revienta en pantalla con «undefined is not a component».
import { Button } from '../ui-v2';
import { Modal } from '../ui-v2.5';
import { editarAlta } from './api';

/** Lo que el servidor dice del estado de la persona, traducido a algo que se pueda leer. */
const ESTADO_TEXTO = {
  1: 'de baja', 2: 'en proceso', 3: 'de alta', 4: 'de baja médica', 5: 'suspendida',
};

const hoyISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export default function EditarAltaDialog({
  open, alta, personaNombre, estadoPersona, onClose, onGuardada,
}) {
  const [fecha, setFecha] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open || !alta) return;
    setFecha(alta.fecha_incorporacion ? String(alta.fecha_incorporacion).slice(0, 10) : '');
    setGuardando(false);
  }, [open, alta]);

  if (!open || !alta) return null;

  const nombre = personaNombre || 'esta persona';
  // El estado de HOY decide si el aviso de acceso tiene sentido siquiera.
  const gobernado = [2, 3].includes(Number(estadoPersona));
  const hoy = hoyISO();
  const eraFutura = alta.fecha_incorporacion ? String(alta.fecha_incorporacion).slice(0, 10) > hoy : false;
  const seraFutura = fecha ? fecha > hoy : false;
  const cambia = !!fecha && fecha !== String(alta.fecha_incorporacion || '').slice(0, 10);

  // El aviso PREVIO es una previsión, y se dice como tal. El hecho lo crea el servidor y se cuenta
  // después con `efecto`. Aquí sólo sirve para que nadie cruce la línea sin verla.
  const preverá = cambia && gobernado && eraFutura !== seraFutura
    ? (seraFutura ? 'perderá' : 'recuperará')
    : null;

  const guardar = async () => {
    if (!fecha) { toast.error('La fecha de incorporación es obligatoria.'); return; }
    setGuardando(true);
    try {
      const r = await editarAlta(alta.id, { fecha_incorporacion: fecha });
      // Lo que le pasó a la persona se DICE, no se supone: es el dato por el que este diálogo
      // avisa antes. Mismo patrón que el de editar la baja (Fase 14).
      const acc = r && r.efecto && r.efecto.accion;
      if (acc === 'activada') {
        toast.success(`Guardado. ${nombre} ya puede entrar en la intranet.`, { autoClose: 8000 });
      } else if (acc === 'desactivada') {
        toast.warn(`Guardado. ${nombre} ha dejado de tener acceso hasta esa fecha.${r.aviso_sesion ? ` ${r.aviso_sesion}` : ''}`, { autoClose: 9000 });
      } else if (r && r.efecto && r.efecto.motivo === 'estado_no_gobernado') {
        toast.info(`Fecha guardada. Su acceso no cambia: ${nombre} está ${ESTADO_TEXTO[r.efecto.estado_actual] || 'en otro estado'}.`, { autoClose: 9000 });
      } else {
        toast.success('Fecha de incorporación actualizada.');
      }
      onGuardada && onGuardada(r);
      onClose && onClose();
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.message) || 'No se ha podido guardar.');
      setGuardando(false);
    }
  };

  return (
    <Modal
      open
      onClose={guardando ? undefined : onClose}
      size="sm"
      tone={preverá === 'perderá' ? 'danger' : 'default'}
      title="Editar el proceso de incorporación"
      subtitle={`${alta.numero || 'Alta'} · ${nombre}`}
      footer={(
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || !fecha}>
            {guardando ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      )}
    >
      <div className="space-y-3">
        {/* La identidad en el CUERPO y no sólo en `subtitle`: la rama `danger` de Modal no pinta
            el subtítulo, así que justo cuando el tono es de aviso la confirmación no diría sobre
            quién actúa. El defecto del componente queda reportado, sin tocarlo. */}
        <p className="rounded-v25-md border border-v25-line bg-v25-surface-sunken px-3 py-2 text-xs">
          <strong className="text-v25-ink-900">{alta.numero}</strong> · {nombre}
        </p>

        <div>
          <label className="mb-1 block text-[12px] font-semibold text-v25-ink-700" htmlFor="alta-fecha">
            Fecha de incorporación
          </label>
          <input
            id="alta-fecha"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="w-full rounded-v25-md border border-v25-line px-3 py-2 text-sm"
          />
          <p className="mt-1 text-[11px] text-v25-ink-500">
            Es la fecha en que empieza a trabajar, y también la que aparece en su ficha. Las dos se
            escriben a la vez.
          </p>
        </div>

        {/* EL AVISO DE ACCESO, antes de guardar. Tres formas, y ninguna se calla. */}
        {!gobernado ? (
          <div className="flex gap-2 rounded-v25-lg border border-v25-line bg-v25-surface-sunken px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-v25-ink-500" />
            <p className="text-xs leading-relaxed text-v25-ink-700">
              {nombre} está <strong className="font-semibold">{ESTADO_TEXTO[Number(estadoPersona)] || 'en otro estado'}</strong>,
              así que cambiar esta fecha <strong className="font-semibold">no le dará ni le quitará el acceso</strong>.
              Un proceso de alta no deshace una baja ni levanta una suspensión.
            </p>
          </div>
        ) : preverá === 'perderá' ? (
          <div className="flex gap-2 rounded-v25-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <UserX className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <p className="text-xs leading-relaxed text-red-800">
              <strong className="font-semibold">{nombre} dejará de tener acceso al guardar.</strong> La
              fecha nueva aún no ha llegado, así que vuelve a «En proceso» hasta ese día.
            </p>
          </div>
        ) : preverá === 'recuperará' ? (
          <div className="flex gap-2 rounded-v25-lg border border-green-200 bg-green-50 px-3 py-2.5">
            <UserCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-700" />
            <p className="text-xs leading-relaxed text-green-800">
              <strong className="font-semibold">{nombre} podrá entrar en la intranet al guardar.</strong> La
              fecha nueva ya ha llegado, así que pasa a «Alta» ahora, sin esperar al proceso nocturno.
            </p>
          </div>
        ) : cambia ? (
          <div className="flex gap-2 rounded-v25-lg border border-v25-line bg-v25-info-bg px-3 py-2.5">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-v25-info-fg" />
            <p className="text-xs leading-relaxed text-v25-ink-700">
              Su acceso no cambia: la fecha nueva cae {seraFutura ? 'en el futuro, igual que la anterior'
                : 'en el pasado, igual que la anterior'}.
            </p>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
